import { CatalogGradeMechanicalLimit } from "@/pages/laboratory/models/CatalogGradeMechanicalLimit";
import { CatalogMechanicalTest } from "@/pages/laboratory/models/CatalogMechanicalTest";
import { CatalogResultSource } from "@/pages/laboratory/models/CatalogResultSource";
import { DynamicMechanicalSample } from "@/pages/laboratory/models/DynamycMechanicalSample";
import { MechanicalSample } from "@/pages/laboratory/models/MechanicalSample";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	CellClassParams,
	ColDef,
	ColGroupDef,
	ValueFormatterParams,
} from "ag-grid-enterprise";
import { useMemo } from "react";

interface UseMechanicalTestTableDataProps {
	mechanicalSamples: MechanicalSample[];
	mechanicalTests: CatalogMechanicalTest[];
	mechanicalGradeLimits: CatalogGradeMechanicalLimit[];
	mechanicalResultSources: CatalogResultSource[];
}

export const useMechanicalTestTableData = ({
	mechanicalSamples,
	mechanicalTests,
	mechanicalGradeLimits,
	mechanicalResultSources,
}: UseMechanicalTestTableDataProps) => {
	// Utility functions
	const numberRichFormatter = (
		params: ValueFormatterParams,
		decimals: number,
	) => {
		if (params.value === null || isNaN(params.value)) return "";
		return parseFloat(params.value).toFixed(decimals);
	};

	const lookupResultSourceValue = (
		resultSources: CatalogResultSource[],
		resultSourceId: string | number,
	) => {
		const id =
			typeof resultSourceId === "string"
				? isNaN(parseInt(resultSourceId))
					? 0
					: parseInt(resultSourceId)
				: resultSourceId;
		const sourceObject = resultSources.find(
			(resultSource) => resultSource.ResultSourceId === id,
		);
		return sourceObject?.ResultSourceName || "";
	};

	const cellLimitClass = (params: CellClassParams) => {
		if (params.value === null || isNaN(params.value)) return "error-limit";
		const sample: DynamicMechanicalSample = params.data;
		if (!sample?.Limits) return "error-limit";

		const limit = sample.Limits.find(
			(limit) =>
				limit.MechanicalTestId === parseInt(params.colDef.field ?? "0"),
		);
		if (!limit) return "error-limit2";

		const { MinValue: minLimit, MaxValue: maxLimit } = limit;
		return (minLimit !== null && parseFloat(params.value) < minLimit) ||
			(maxLimit !== null && parseFloat(params.value) > maxLimit)
			? "error-limit"
			: "error-limit2";
	};

	// Sample information columns (all sample-related data)
	const sampleColumns: ColGroupDef = useMemo(
		() => ({
			headerName: "Sample",
			children: [
				{
					field: "HeatName",
					headerName: "Heat",
					minWidth: 78,
					flex: 1,
				},
				{
					field: "SampleSequence",
					headerName: "Seq",
					minWidth: 50,
					flex: 1,
				},
				{
					field: "GradeId",
					headerName: "Grade",
					minWidth: 86,
					flex: 1,
				},
				{
					field: "SectionId",
					headerName: "Section",
					minWidth: 96,
					flex: 1,
				},
				{
					field: "Width",
					headerName: "Width",
					minWidth: 85,
					type: ["numericColumn"],
					valueFormatter: (params) => numberRichFormatter(params, 4),
					headerClass: "stripe-header",
					flex: 1,
				},
				{
					field: "Thickness",
					headerName: "Thickness",
					minWidth: 113,
					type: ["numericColumn"],
					valueFormatter: (params) => numberRichFormatter(params, 4),
					headerClass: "stripe-header",
					flex: 1,
				},
				{
					field: "ResultSourceId",
					headerName: "Source",
					minWidth: 93,
					flex: 1,
					valueFormatter: (params) =>
						lookupResultSourceValue(mechanicalResultSources, params.value),
				},
				{
					field: "Method",
					headerName: "Method",
					minWidth: 97,
					flex: 1,
				},
			],
		}),
		[mechanicalResultSources],
	);

	// Dynamic test columns only
	const testColumns: ColGroupDef = useMemo(() => {
		const dynamicTestColumns: ColDef[] = mechanicalTests
			.filter((test) => test.IsActive)
			.map((test) => ({
				field: test.MechanicatTestId?.toString(),
				headerName: getTestDisplayName(test.MechanicalTestName),
				minWidth: 80,
				flex: 1,
				type: ["numericColumn"],
				headerClass: test.UseInCircular ? "stripe-header" : "",
				valueFormatter: (params) => numberRichFormatter(params, test.Decimals),
				cellClass: cellLimitClass,
			}));

		return {
			headerName: "Tests",
			children: dynamicTestColumns,
		};
	}, [mechanicalTests]);

	// Results columns
	const resultColumns: ColGroupDef = useMemo(
		() => ({
			headerName: "Results",
			children: [
				{
					field: "DateTimeResults",
					headerName: "Result",
					valueGetter: (params) =>
						dxtToLocalServerTime(
							`${params.data.DateTimeResults}`,
							"MM/dd/yyyy - HH:mm",
						) || "",
					minWidth: 150,
					flex: 1,
				},
				{
					field: "Technician",
					headerName: "Technician",
					minWidth: 117,
					flex: 1,
				},
			],
		}),
		[],
	);

	// Transform sample data for AG-Grid
	const transformedRows = useMemo(() => {
		return mechanicalSamples.map((sample) => {
			const dynamicSample: DynamicMechanicalSample = {
				...sample,
				elementParams: [],
				sampleParams: [
					"SampleSequence",
					"DateTimeResults",
					"StatusCode",
					"ResultSourceId",
					"Comments",
					"Technician",
					"Method",
					"HeatId",
					"Width",
					"Thickness",
					"Diameter",
				],
				testParams: [],
			};

			const samplesId = (sample.SamplesId ?? "").split(",");
			const samplesValue = (sample.SamplesValue ?? "").split(",");
			const samplesResult = (sample.ResultsId ?? "").split(",");

			// Set grade limits
			dynamicSample.Limits = mechanicalGradeLimits.filter(
				(limit) => limit.GradeId === sample.GradeId,
			);

			// Initialize mechanical tests with null values
			mechanicalTests.forEach((test) => {
				dynamicSample[String(test.MechanicatTestId).trim()] = null;
			});

			// Set sample values
			samplesId.forEach((id, index) => {
				const value = samplesValue[index];
				dynamicSample[id.trim()] = !isNaN(parseFloat(value))
					? parseFloat(value)
					: null;
			});

			return dynamicSample;
		});
	}, [mechanicalSamples, mechanicalTests, mechanicalGradeLimits]);

	// Main Mechanical Tests group that contains all other groups as children
	const mechanicalTestsGroup: ColGroupDef = useMemo(
		() => ({
			headerName: "Mechanical",
			children: [sampleColumns, testColumns, resultColumns],
		}),
		[sampleColumns, testColumns, resultColumns],
	);

	// Combine all columns
	const columns = useMemo(() => [mechanicalTestsGroup], [mechanicalTestsGroup]);

	// Default column definitions
	const defaultColDef = useMemo(
		() => ({
			flex: 1,
			resizable: true,
			sortable: true,
			filter: true,
			editable: false,
			tooltipComponent: "customTooltip",
			tooltipComponentParams: { color: "#ececec" },
		}),
		[],
	);

	return {
		columns,
		rows: transformedRows,
		defaultColDef,
	};
};

// Helper function to get friendly test names
const getTestDisplayName = (testName: string): string => {
	const displayNames: { [key: string]: string } = {
		"Yield Strength": "Yield",
		"Tensile Strength": "Tensile",
		Elongation: "Elongation",
		"Reduction of Area": "RA",
	};
	return displayNames[testName] || testName;
};
